import { Policy } from '../types/policy.types';
import { useState, useCallback, useEffect, useRef } from 'react';

export const usePolicyEditorState = (
	policy: Policy | null,
	onChange?: (updatedPolicy: Partial<Policy>) => void
) => {
	const [title, setTitle] = useState(policy?.title || '');
	const [description, setDescription] = useState(policy?.description || '');
	const [content, setContent] = useState(policy?.content || '');
	const [version, setVersion] = useState(policy?.versionNumber || '1.0');
	const [effectiveDate, setEffectiveDate] = useState(policy?.effectiveDate || '');

	const policyIdRef = useRef(policy?._id);

	useEffect(() => {
		if (policy && policy._id !== policyIdRef.current) {
			policyIdRef.current = policy._id;
			setTitle(policy.title || '');
			setDescription(policy.description || '');
			setContent(policy.content);
			setVersion(policy.versionNumber);
			setEffectiveDate(policy.effectiveDate);
		}
	}, [policy]);

	const notifyChange = useCallback(
		(field: keyof Policy, value: string) => {
			if (onChange && policy) {
				onChange({
					...policy,
					[field]: value,
				});
			}
		},
		[onChange, policy]
	);

	const handleTitleChange = useCallback(
		(value: string) => {
			setTitle(value);
			notifyChange('title', value);
		},
		[notifyChange]
	);

	const handleDescriptionChange = useCallback(
		(value: string) => {
			setDescription(value);
			notifyChange('description', value);
		},
		[notifyChange]
	);

	const handleContentChange = useCallback(
		(value: string) => {
			setContent(value);
			notifyChange('content', value);
		},
		[notifyChange]
	);

	const handleVersionChange = useCallback(
		(value: string) => {
			setVersion(value);
			notifyChange('versionNumber', value);
		},
		[notifyChange]
	);

	const handleEffectiveDateChange = useCallback(
		(value: string) => {
			setEffectiveDate(value);
			notifyChange('effectiveDate', value);
		},
		[notifyChange]
	);

	return {
		title,
		description,
		content,
		version,
		effectiveDate,
		handleTitleChange,
		handleDescriptionChange,
		handleContentChange,
		handleVersionChange,
		handleEffectiveDateChange,
	};
};
